import { yupResolver } from '@hookform/resolvers/yup';
import { HubConnection, HubConnectionBuilder, JsonHubProtocol, LogLevel } from '@microsoft/signalr';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, Button, Card, CardContent, Dialog, DialogContent, Grid, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { BEEScorecardService } from '../../../../Component/Api/BEEScorecard/BEEScorecard';
import { CoreImportService, CentralImportData } from '../../../../Component/Api/Import/CoreImportService';
import { ImportService } from '../../../../Component/Api/Import/ImportService';
import AppDropzone from '../../../../Component/Common/Components/FormValidationInputs/AppDropZone';
import AppMultiSelect from '../../../../Component/Common/Components/FormValidationInputs/AppMultiSelect';
import AppSelectList from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import { IntermediateGlobalLoading } from '../../../../Component/Common/Components/Loading/IntermediateGlobalLoading';
import { MDAlert } from '../../../../Component/Common/Components/MDAlert';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { ImportProgressBar } from '../../../../Component/Common/Components/ProgressBar/ImportProgressBar';
import { TooltipLoadingButton } from '../../../../Component/Common/Components/Tooltip/TooltipLoadingButton';
import { GetAllScorecardsResponse } from '../../../../Component/Model/Dto/ScorecardLicense';
import { ExportKeyValue } from '../../../../Component/Model/Dto/Upload';
import { DataImportType } from '../../../../Component/Model/Enum/BEE123Enums';
import { ImportCommonFunctionsDropDown, dataImportTypeLookupVal } from '../../../../Helpers/ImportHelper';
import { getIOptionsArray } from '../../../../Helpers/arrayHelper';
import { defaultObject, emptyGuid } from '../../../../Helpers/typescriptHelper';
import { UserAtom } from '../../../../State/user.Atom';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { DataImportTypesCode } from '../../../Common/ImportAndExport/ImportPopup';
import { IndividualSalariesTemplateModal } from '../../IndividualSalaries/IndividualSalariesTemplateModal';
import { ProgressData } from '../IndividualImport/IndividualImport';
import { IndividualImportValidationSchema } from '../IndividualImport/IndividualImportValidationSchema';
import { BEE123SalariesEditRole, EE123SalariesRole_Edit } from '../../../../Component/Common/Constants/UserRoles'
import { userRolesState } from '../../../../State/user.Atom'
import { ImportSource } from "../../../../Component/Model/Enum/ImportEnum";
import {useSignalRConnection} from "../../../../Hooks/useSignalRConnection";
import { analytics } from '../../../../Services/analytics';

export const IndividualSalariesImport = () => {
  const user = useRecoilValue(UserAtom);
  const [scorecardData, setScorecardData] = useState<GetAllScorecardsResponse>(defaultObject);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [scorecardList, setScorecardList] = useState<string[]>([]);
  const [progressData, setProgressData] = useState<{ [key: string]: ProgressData }>({});
  const [watchFile, setWatchFile] = useState(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const hub = useSignalRConnection("ImportHub");

  const { control, handleSubmit, watch, setValue, formState: { isSubmitting } } = useForm({
    mode: 'all',
    resolver: yupResolver(IndividualImportValidationSchema)
  });

  useEffect(() => {
    getAllScorecardData();
  }, []);
  const getAllScorecardData = async () => {
    try {
      setIsLoading(true);
      const result = await BEEScorecardService.getAllScorecards();
      setScorecardData(result.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  const clearFile = () => {
    setValue('file', null);
    setWatchFile(null);
  };

  useEffect(() => {
    if (scorecardList.length == 0) return;

    (async () => {
      try {
        if(!hub.isConnected)
        {
          await hub.start();
        }

        hub.on(`importProgress-${user.username}-${DataImportType[DataImportType.IndividualSalary]}`, (data: any) => {
          const { CurrentScorecard, progress, LastSuccessfulStep } = data;
          setProgressData((prevData) => ({
            ...prevData, [CurrentScorecard]: {
              ...prevData[CurrentScorecard],
              progress,
              lastSuccessfulStep: LastSuccessfulStep
            }
          }));
        });
        hub.on(`importProgressFailed-${user.username}-${DataImportType[DataImportType.IndividualSalary]}`, (data: any) => {
          const { CurrentScorecard, NumberOfRowsCorrect, NumberOfRowsFailed, Log, ErrorBlobUrl, LastSuccessfulStep } = data;
          setProgressData((prevData) => ({
            ...prevData,
            [CurrentScorecard]: {
              progress: 0,
              isSuccess: false,
              numberOfRowsCorrect: NumberOfRowsCorrect,
              numberOfRowsFailed: NumberOfRowsFailed,
              log: Log,
              errorLogUrl: ErrorBlobUrl,
              lastSuccessfulStep: LastSuccessfulStep
            }
          }));
          const currentIndex = scorecardList.indexOf(CurrentScorecard);
          if (currentIndex < scorecardList.length - 1) {
            const nextScorecardId = scorecardList[currentIndex + 1];
            // Start progress for the next scorecardId
            setProgressData((prevData) => ({
              ...prevData,
              [nextScorecardId]: {
                progress: 0,
                isSuccess: true,
                numberOfRowsCorrect: 0,
                numberOfRowsFailed: 0,
                log: []
              }
            }));
          }
        });
        hub.on(`importProgressSuccess-${user.username}-${DataImportType[DataImportType.IndividualSalary]}`, (data: any) => {
          const { CurrentScorecard, NumberOfRowsCorrect, NumberOfRowsFailed, Log } = data;
          setProgressData((prevData) => ({
            ...prevData,
            [CurrentScorecard]: {
              progress: 100,
              isSuccess: true,
              numberOfRowsCorrect: NumberOfRowsCorrect,
              numberOfRowsFailed: NumberOfRowsFailed,
              log: Log
            }
          }));
          const currentIndex = scorecardList.indexOf(CurrentScorecard);
          if (currentIndex < scorecardList.length - 1) {
            const nextScorecardId = scorecardList[currentIndex + 1];
            // Start progress for the next scorecardId
            setProgressData((prevData) => ({
              ...prevData,
              [nextScorecardId]: {
                progress: 0,
                isSuccess: true,
                numberOfRowsCorrect: 0,
                numberOfRowsFailed: 0,
                log: []
              }
            }));
          }

        });
        hub.on(`ImportComplete-${user.username}-${DataImportType[DataImportType.IndividualSalary]}`, () => {
          setIsImporting(false);
        });

        hub.on(`importProgress-${user.username}-${DataImportType[DataImportType.EESalary]}`, (data: any) => {
          const { CurrentScorecard, progress, LastSuccessfulStep } = data;
          setProgressData((prevData) => ({
            ...prevData, [CurrentScorecard]: {
              ...prevData[CurrentScorecard],
              progress,
              lastSuccessfulStep: LastSuccessfulStep
            }
          }));
        });
        hub.on(`importProgressFailed-${user.username}-${DataImportType[DataImportType.EESalary]}`, (data: any) => {
          const { CurrentScorecard, NumberOfRowsCorrect, NumberOfRowsFailed, Log, ErrorBlobUrl, LastSuccessfulStep } = data;
          setProgressData((prevData) => ({
            ...prevData,
            [CurrentScorecard]: {
              progress: 0,
              isSuccess: false,
              numberOfRowsCorrect: NumberOfRowsCorrect,
              numberOfRowsFailed: NumberOfRowsFailed,
              errorLogUrl: ErrorBlobUrl,
              log: Log,
              lastSuccessfulStep: LastSuccessfulStep
            }
          }));
          const currentIndex = scorecardList.indexOf(CurrentScorecard);
          if (currentIndex < scorecardList.length - 1) {
            const nextScorecardId = scorecardList[currentIndex + 1];
            // Start progress for the next scorecardId
            setProgressData((prevData) => ({
              ...prevData,
              [nextScorecardId]: {
                progress: 0,
                isSuccess: true,
                numberOfRowsCorrect: 0,
                numberOfRowsFailed: 0,
                log: []
              }
            }));
          }
        });
        hub.on(`importProgressSuccess-${user.username}-${DataImportType[DataImportType.EESalary]}`, (data: any) => {
          const { CurrentScorecard, NumberOfRowsCorrect, NumberOfRowsFailed, Log } = data;
          setProgressData((prevData) => ({
            ...prevData,
            [CurrentScorecard]: {
              progress: 100,
              isSuccess: true,
              numberOfRowsCorrect: NumberOfRowsCorrect,
              numberOfRowsFailed: NumberOfRowsFailed,
              log: Log
            }
          }));
          const currentIndex = scorecardList.indexOf(CurrentScorecard);
          if (currentIndex < scorecardList.length - 1) {
            const nextScorecardId = scorecardList[currentIndex + 1];
            // Start progress for the next scorecardId
            setProgressData((prevData) => ({
              ...prevData,
              [nextScorecardId]: {
                progress: 0,
                isSuccess: true,
                numberOfRowsCorrect: 0,
                numberOfRowsFailed: 0,
                log: []
              }
            }));
          }

        });
        hub.on(`ImportComplete-${user.username}-${DataImportType[DataImportType.EESalary]}`, () => {
          setIsImporting(false);
        });
      } catch (error) {
        console.error("SignalR connection failed:", error);
      }
    })();
  }, [scorecardList]);

  const disableImportButton = (): boolean => {
    if (scorecardData.eeScorecards === undefined && scorecardData.beeScorecards === undefined) {
      return true;
    }
    return scorecardData.eeScorecards?.length === 0 && scorecardData.beeScorecards?.length === 0;
  }

  const percentageUploadChange = (progress: number) => {

  };

  const handleDataSubmit = async (data: FieldValues) => {
    setProgressData(defaultObject);
    setIsImporting(true);
    try {
      const parameters: ExportKeyValue[] = [];
      data.eeScorecards = data.eeScorecards || [];
      data.beeScorecards = data.beeScorecards || [];
      const scorecardIds = [...data.beeScorecards, ...data.eeScorecards];
      setScorecardList(scorecardIds);
      const request: CentralImportData = {
        importType: DataImportType.IndividualSalary,
        scorecardIds: scorecardIds,
        importSource: ImportSource.CDM,
        importAction: parseInt(data.importType),
        file: data.file,
        parameters: parameters,
        setPercentageUpload: (progress: number) => percentageUploadChange(progress),
      };
      await CoreImportService.ImportCentralFile(request);
      analytics.cdmManualDataImported(DataImportType[DataImportType.IndividualSalary]);
    } catch (error) {
      console.log(error);
      setIsImporting(false);
      toast.error('Import Failed');
    }
  }

  const findScorecardName = (scorecardId: string): string => {
    const scorecard = scorecardData.beeScorecards.find((x) => x.scorecardId === scorecardId);
    if (scorecard) {
      return scorecard.scorecardName;
    } else {
      const scorecard = scorecardData.eeScorecards.find((x) => x.scorecardId === scorecardId);
      if (scorecard) {
        return scorecard.scorecardName;
      }
    }
    return "";
  }

  const openTemplateModal = () => {
    setOpenModal(true);
  }

  const closeTemplateModal = () => {
    setOpenModal(false);
  }

  const getImportHelpFile = (e: any) => {
    const sectionId = dataImportTypeLookupVal(DataImportType[DataImportType.EESalary], DataImportTypesCode.name, DataImportTypesCode.id);
    if (sectionId?.toString() !== undefined) {
      ImportService.DownloadImportHelpfile(emptyGuid, sectionId.toString());
    }
  };

    const userRoles = useRecoilValue(userRolesState);
    const hasBEEImportRole = userRoles.includes(BEE123SalariesEditRole);
    const hasEEImportRole = userRoles.includes(EE123SalariesRole_Edit);
  
  const file = watch('file');
  if (isLoading) {
    return <IntermediateGlobalLoading />;
  };
  return (
    <>
      <Grid>
        <Typography variant="body2">Please note, locked and expired scorecards are not available for selection. </Typography>
      </Grid>
      <Grid container spacing={3} component="form" id='IndividualSalariesImport' onSubmit={handleSubmit(handleDataSubmit)}>
        <Grid item xs={6}>
          {scorecardData.beeScorecards && scorecardData.beeScorecards.length > 0 && (
            <Box mt={2}>
              <AppMultiSelect disabled={isImporting || !hasBEEImportRole} control={control} name="beeScorecards" label="BEE Scorecards" options={getIOptionsArray(scorecardData.beeScorecards, "scorecardId", "scorecardName")} size="small" />
              {!hasBEEImportRole &&
              <Typography style={{ fontSize: '15px', color: 'red' }}>
                You don't have permission to edit BEE Salaries
              </Typography>}
            </Box>
          )}
          {scorecardData.eeScorecards && scorecardData.eeScorecards.length > 0 && (
            <Box mt={2}>
              <AppMultiSelect disabled={isImporting || !hasEEImportRole} control={control} name="eeScorecards" label="EE Scorecards" options={getIOptionsArray(scorecardData.eeScorecards, "scorecardId", "scorecardName")} size="small" />
              {!hasEEImportRole &&
              <Typography style={{ fontSize: '15px', color: 'red' }}>
                You don't have permission to edit EE Salaries
              </Typography>}
            </Box>
          )}
          <Box mt={2}>
            {disableImportButton() &&
              <Typography style={{ fontSize: '15px', color: 'red', paddingTop: '30px', paddingBottom: '20px' }}>
                You don't have any scorecards assigned to you, please contact your administrator to create a scorecard
              </Typography>
            }
          </Box>
          <Box mt={2}>
            <AppSelectList disabled={isImporting} control={control} name="importType" label="Import Type" items={ImportCommonFunctionsDropDown(DataImportType[DataImportType.IndividualSalary])} />
            <MDBox mt={4} color="white">
              <MDAlert color='light'>
                <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>
                  <p style={{ marginBottom: "0.2rem" }}> Click<Button disabled={isImporting} variant="text" onClick={getImportHelpFile} size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                    for import instructions</p>
                  <p style={{ marginBottom: "0.2rem" }}> Click<Button disabled={isImporting} variant="text" onClick={() => openTemplateModal()} size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                    to download the template file</p>
                </MDTypography>
              </MDAlert>
            </MDBox>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box mt={2} display='flex' justifyContent='space-between' alignItems='center'>
            {!file && <AppDropzone control={control} name="file" />}
            {file && (
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
                  <Box position="relative">
                    <img src={`${process.env.PUBLIC_URL}/img/csv-icon.png`} alt='preview' style={{ maxHeight: '200px' }} />
                    <IconButton
                      disabled={isImporting}
                      onClick={clearFile}
                      style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        zIndex: 1
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Box>

                </Box>
                <MDTypography variant="caption" align="center">{file.name}</MDTypography>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={5}>
          <TooltipLoadingButton buttonText="Begin Import" form="IndividualSalariesImport" tooltipTitle="No scorecards available" disabled={disableImportButton()} isLoading={isImporting} showTooltipOnDisabled={true} />
        </Grid>
        <Grid item xs={12}>
          <Box mt={2}>
            {scorecardList.map((scorecardId) => (
              <Box key={scorecardId} mb={2}>
                <Card key={scorecardId} variant="outlined">
                  <CardContent>
                    <MDTypography variant="h6">{findScorecardName(scorecardId)}</MDTypography>
                    <Box sx={{ minWidth: 40 }}>
                      <Typography variant="body2" color="text.secondary">{`${progressData[scorecardId]?.lastSuccessfulStep ?? ""}`}</Typography>
                    </Box>
                    <ImportProgressBar
                      value={progressData[scorecardId]?.progress ?? 0}
                      progressData={progressData[scorecardId]}
                      errorLogUrl={progressData[scorecardId]?.errorLogUrl}
                    />
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>

      <Dialog open={openModal} onClose={() => closeTemplateModal()} aria-labelledby="form-dialog-title" fullWidth>
        <CustomDialogTitle id="customized-dialog-title" onClose={() => closeTemplateModal()}>
          Download Salary Template
        </CustomDialogTitle>
        <DialogContent>
          <IndividualSalariesTemplateModal closeTemplateModal={closeTemplateModal} />
        </DialogContent>
      </Dialog>
    </>
  )
}



